import { ChangeDetectorRef, Directive, HostListener, inject, OnDestroy } from '@angular/core';
import { FormGroup, ValidatorFn } from '@angular/forms';

import { BehaviorSubject, pairwise, takeUntil } from 'rxjs';

import { ScreenBreakpointPX } from '@summit/core';
import { SummitDisposing } from '@summit/models';
import { ApiError } from '@summit/ui';

export interface ISummitForm {
  elementForm?: FormGroup;
  hasUnSavedData(): boolean;
}

@Directive()
export abstract class SummitFormBase extends SummitDisposing implements ISummitForm, OnDestroy {
  elementForm?: FormGroup;
  apiError: ApiError = new ApiError();
  nonFormDataDirty = false;
  submittingBs = new BehaviorSubject<boolean>(false);
  submitting$ = this.submittingBs.asObservable();
  protected cd: ChangeDetectorRef;
  protected skipUnloadTest?: boolean;
  ScreenBreakpoint = ScreenBreakpointPX;

  // Validator initialization control - prevents excessive validator calls during form setup
  protected isInitializing = true;

  constructor() {
    super();
    this.cd = inject(ChangeDetectorRef);
  }

  hasUnSavedData(): boolean {
    /*
    console.log(
      `Testing Unsaved Data: submitting: ${this.submitting$.value}, form: ${this.elementForm.dirty}, non-form:${this.nonFormDataDirty}`,
      this.elementForm
    );
    Object.keys(this.elementForm?.controls ?? {}).forEach((key) => {
      console.log(`${key}:`, this.elementForm?.controls[key]?.dirty);
    });
    */
    return !this.submittingBs.value && (this.elementForm?.dirty || this.nonFormDataDirty);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: Event) {
    if (!this.skipUnloadTest && this.hasUnSavedData()) {
      $event.returnValue = true;
    }
  }

  protected preSaveCheck(): boolean {
    const debugging = false;
    this.submittingBs.next(true);
    this.elementForm?.updateValueAndValidity();
    if (this.elementForm?.invalid) {
      this.elementForm?.markAllAsTouched();
      //console.log('detecting changes');
      this.cd.detectChanges();
      if (debugging) console.log('disabling submit');
      this.submittingBs.next(false);
      console.warn('Form is invalid, not submitting.', this.submittingBs.value);
      //console.log(`Form Errors: `, this.elementForm.errors, this.elementForm);
      // Object.keys(this.elementForm.controls).forEach((key) => {
      //   const control = this.elementForm.get(key);
      //   //if (control.errors) console.log(`${key}:`, control.errors);
      // });
      return false;
    }
    return true;
  }

  override ngOnDestroy(): void {
    this.submittingBs.next(false);
    this.submittingBs.complete();
    // Not sure if this is cascaded or not
    this.disposing$.next(null);
    this.disposing$.complete();
    super.ngOnDestroy();
  }

  addValidators(validators: ValidatorFn) {
    // console.debug('Adding validators with wrapper:', validators.name);
    this.elementForm?.addValidators(this.wrapValidator(validators));
  }

  configureForm() {
    // NOTE: apiErrorValidator removed - ApiError.ProcessServerError already sets control errors
    // This subscription cleans up API errors when users edit fields
    this.elementForm?.valueChanges.pipe(pairwise(), takeUntil(this.disposing$)).subscribe(([oldValue, newValue]) => {
      const apiErrorModel = this.apiError?.model;
      if (apiErrorModel) {
        Object.keys(apiErrorModel).forEach((key) => {
          const lclControl = this.elementForm?.get(key);
          if (lclControl?.errors?.['apiError'] && oldValue[key] !== newValue[key]) {
            if (apiErrorModel[key]) delete apiErrorModel[key];
            // Remove the apiError without triggering validation
            const currentErrors = { ...lclControl.errors };
            delete currentErrors['apiError'];
            const hasOtherErrors = Object.keys(currentErrors).length > 0;
            lclControl.setErrors(hasOtherErrors ? currentErrors : null, { emitEvent: false });
          }
        });
      }
    });
  }

  /**
   * Wraps a validator function to skip execution during form initialization.
   * Use this wrapper when adding custom validators that should not run during initial form setup.
   *
   * @example
   * this.elementForm.addValidators(this.wrapValidator(this.customValidators.bind(this)));
   */
  protected wrapValidator(validatorFn: ValidatorFn): ValidatorFn {
    return (control) => {
      if (this.isInitializing) {
        //console.log('[SummitFormBase] wrapValidator - SKIPPING validator during initialization:', validatorFn.name);
        return null; // Skip validation during initialization
      }
      //console.log('[SummitFormBase] wrapValidator - RUNNING validator:', validatorFn.name);
      return validatorFn(control);
    };
  }

  /**
   * Call this method at the end of component initialization to enable validators.
   * Components that override ngAfterViewInit with async initialization should call this
   * after their setup is complete.
   */
  protected finishInitialization(): void {
    //console.log('[SummitFormBase] finishInitialization - CALLED, isInitializing was:', this.isInitializing);
    this.isInitializing = false;
    //console.log('[SummitFormBase] finishInitialization - COMPLETE, isInitializing now:', this.isInitializing);
  }
}
